import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDollarSign, FiCalendar, FiTrendingUp, FiHome } from 'react-icons/fi';
import api from '../services/api';
import PropertyCalendar from '../components/PropertyCalendar';
import DateBlockManager from '../components/DateBlockManager';
import RevenueChart from '../components/RevenueChart';
import './OwnerPropertyDetail.css';

const OwnerPropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [period, setPeriod] = useState('month'); // month, year, custom

  useEffect(() => {
    fetchPropertyData();
  }, [id, period]);

  const fetchPropertyData = async () => {
    try {
      setLoading(true);

      // Calculate date range based on period
      const today = new Date();
      let startDate, endDate;

      if (period === 'month') {
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      } else if (period === 'year') {
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today.getFullYear(), 11, 31);
      }

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      // Fetch all data in parallel
      const [dashboardRes, revenueRes] = await Promise.all([
        api.get(`/api/owners/properties/${id}/dashboard?start_date=${startDateStr}&end_date=${endDateStr}`),
        api.get(`/api/owners/properties/${id}/revenue?start_date=${startDateStr}&end_date=${endDateStr}`)
      ]);

      setProperty(dashboardRes.data.property);
      setDashboard(dashboardRes.data);
      setRevenue(revenueRes.data);
    } catch (error) {
      console.error('Error fetching property data:', error);
      if (error.response?.status === 403) {
        alert('Você não tem permissão para acessar este imóvel');
        navigate('/owner/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="owner-property-detail">
        <div className="loading-container">
          <p>Carregando dados do imóvel...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="owner-property-detail">
        <div className="error-container">
          <p>Imóvel não encontrado</p>
          <button onClick={() => navigate('/owner/dashboard')} className="btn btn-primary">
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="owner-property-detail">
      {/* Header */}
      <div className="detail-header">
        <button onClick={() => navigate('/owner/dashboard')} className="btn-back">
          <FiArrowLeft /> Voltar
        </button>
        <div className="header-info">
          <h1>{property.title}</h1>
          <p className="property-location">
            {property.city}, {property.state && `${property.state}, `}{property.country}
          </p>
          {property.host && (
            <p className="property-host">
              Gerenciado por: <strong>{property.host.first_name} {property.host.last_name}</strong>
            </p>
          )}
        </div>
        <div className="header-actions">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="period-selector"
          >
            <option value="month">Este Mês</option>
            <option value="year">Este Ano</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      {dashboard?.stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#e7f3ff' }}>
              <FiDollarSign style={{ color: '#007bff' }} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Receita Total</p>
              <p className="stat-value">
                R$ {dashboard.stats.total_revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="stat-meta">
                {dashboard.stats.total_bookings} reservas
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fff0e7' }}>
              <FiTrendingUp style={{ color: '#ff9800' }} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Taxa de Ocupação</p>
              <p className="stat-value">{dashboard.stats.occupancy_rate}%</p>
              <p className="stat-meta">
                {dashboard.stats.occupied_days} de {dashboard.stats.total_days} dias
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#e7f9f0' }}>
              <FiCalendar style={{ color: '#28a745' }} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Dias Disponíveis</p>
              <p className="stat-value">{dashboard.stats.available_days}</p>
              <p className="stat-meta">Prontos para reserva</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fff0f0' }}>
              <FiHome style={{ color: '#dc3545' }} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Valor Médio/Reserva</p>
              <p className="stat-value">
                R$ {dashboard.stats.average_booking_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="stat-meta">Por estadia</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`tab-button ${activeTab === 'revenue' ? 'active' : ''}`}
          onClick={() => setActiveTab('revenue')}
        >
          Faturamento
        </button>
        <button
          className={`tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Calendário
        </button>
        <button
          className={`tab-button ${activeTab === 'blocks' ? 'active' : ''}`}
          onClick={() => setActiveTab('blocks')}
        >
          Bloqueios
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'dashboard' && (
          <div className="dashboard-tab">
            {revenue?.monthly_breakdown && (
              <RevenueChart monthlyData={revenue.monthly_breakdown} />
            )}

            {dashboard?.upcoming_bookings && dashboard.upcoming_bookings.length > 0 && (
              <div className="upcoming-bookings-section">
                <h3>Próximas Reservas</h3>
                <div className="bookings-list">
                  {dashboard.upcoming_bookings.map(booking => (
                    <div key={booking.id} className="booking-card">
                      <div className="booking-dates">
                        <div className="date-item">
                          <span className="date-label">Check-in:</span>
                          <span className="date-value">
                            {new Date(booking.check_in).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        <div className="date-item">
                          <span className="date-label">Check-out:</span>
                          <span className="date-value">
                            {new Date(booking.check_out).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                      <div className="booking-info">
                        <p><strong>{booking.guest.first_name} {booking.guest.last_name}</strong></p>
                        <p>{booking.num_guests} hóspedes • {booking.nights} noites</p>
                        <p className="booking-price">
                          R$ {booking.total_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'revenue' && revenue && (
          <div className="revenue-tab">
            <div className="revenue-summary">
              <h3>Resumo de Faturamento</h3>
              <div className="summary-cards">
                <div className="summary-card">
                  <p className="summary-label">Receita Total</p>
                  <p className="summary-value">
                    R$ {revenue.summary.total_revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="summary-card">
                  <p className="summary-label">Total de Reservas</p>
                  <p className="summary-value">{revenue.summary.total_bookings}</p>
                </div>
                <div className="summary-card">
                  <p className="summary-label">Total de Noites</p>
                  <p className="summary-value">{revenue.summary.total_nights}</p>
                </div>
                <div className="summary-card">
                  <p className="summary-label">Diária Média</p>
                  <p className="summary-value">
                    R$ {revenue.summary.average_nightly_rate.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>

            <RevenueChart monthlyData={revenue.monthly_breakdown} />

            <div className="bookings-detail">
              <h3>Histórico de Reservas</h3>
              <div className="bookings-table">
                <table>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Hóspede</th>
                      <th>Noites</th>
                      <th>Hóspedes</th>
                      <th>Valor</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenue.bookings.map(booking => (
                      <tr key={booking.id}>
                        <td>{new Date(booking.check_in).toLocaleDateString('pt-BR')}</td>
                        <td>{booking.guest.name}</td>
                        <td>{booking.nights}</td>
                        <td>{booking.num_guests}</td>
                        <td>R$ {booking.total_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                        <td>
                          <span className={`status-badge ${booking.status}`}>
                            {booking.status === 'completed' ? 'Concluído' : booking.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'calendar' && (
          <div className="calendar-tab">
            <PropertyCalendar
              propertyId={id}
              apiEndpoint="/api/owners"
            />
          </div>
        )}

        {activeTab === 'blocks' && (
          <div className="blocks-tab">
            <DateBlockManager
              propertyId={id}
              apiEndpoint="/api/owners"
              onUpdate={fetchPropertyData}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerPropertyDetail;
